/**
 * Authentication Context using MSAL (Microsoft Authentication Library).
 *
 * Uses client-side MSAL for Azure AD OAuth and Bearer token authentication.
 * Unified login flow for all users (home tenant and federated organizations).
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/msalConfig';
import { User } from '../types/auth';

// localStorage key for storing the last logged-in user's email
export const LAST_USER_EMAIL_KEY = 'irpilot_last_user_email';

/**
 * Authentication context interface.
 *
 * Provides authentication state and methods to child components.
 */
interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isSsoAttempting: boolean;
    authError: string | null;
    login: (loginHint?: string, forceAccountSelection?: boolean) => Promise<void>;
    logout: () => Promise<void>;
    switchAccount: () => Promise<void>;
    getAccessToken: () => Promise<string>;
    clearAuthError: () => void;
}

// Exported for test providers - allows tests to provide auth state without vi.mock()
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for AuthProvider component.
 */
interface AuthProviderProps {
    children: ReactNode;
}

/**
 * Authentication Provider component.
 *
 * Wraps the application to provide authentication context using MSAL.
 * Uses MSAL for Azure AD OAuth and Bearer token management.
 *
 * @param props - Component props with children
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSsoAttempting, setIsSsoAttempting] = useState<boolean>(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const ssoAttempted = useRef<boolean>(false);

    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:9000';

    /**
     * Clear authentication error.
     */
    const clearAuthError = (): void => {
        setAuthError(null);
    };

    /**
     * Fetch user information from backend using Bearer token.
     */
    const fetchUserInfo = async (accessToken: string, accountUpn: string): Promise<void> => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setAuthError(null);
                // Store email for "Welcome back" experience on future visits
                if (userData.email) {
                    localStorage.setItem(LAST_USER_EMAIL_KEY, userData.email);
                }
            } else if (response.status === 403) {
                setUser(null);
                setAuthError(
                    `The account ${accountUpn} is not registered with this system. ` +
                    `Please check the account to confirm it has access.`
                );
            } else {
                setUser(null);
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch user info:', error);
            setUser(null);
        }
    };

    /**
     * Get access token for API requests.
     * Uses silent token acquisition with automatic retry on failure.
     */
    const getAccessToken = async (): Promise<string> => {
        if (!isAuthenticated || accounts.length === 0) {
            throw new Error('User not authenticated');
        }

        const account = accounts[0];

        try {
            // Try silent token acquisition first
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account,
            });
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Silent acquisition failed, try popup
                try {
                    const response = await instance.acquireTokenPopup({
                        ...loginRequest,
                        account,
                    });
                    return response.accessToken;
                } catch (popupError) {
                    // eslint-disable-next-line no-console
                    console.error('Token acquisition failed:', popupError);
                    throw new Error('Failed to acquire access token');
                }
            } else {
                // eslint-disable-next-line no-console
                console.error('Token acquisition error:', error);
                throw new Error('Failed to acquire access token');
            }
        }
    };

    /**
     * Initialize user info when authenticated.
     */
    useEffect(() => {
        const initializeUser = async () => {
            if (isAuthenticated && accounts.length > 0 && inProgress === InteractionStatus.None) {
                // CRITICAL: Set the active account so getActiveAccount() works in hooks
                // Without this, useDelegatedToken and other hooks calling getActiveAccount() will get null
                instance.setActiveAccount(accounts[0]);

                // Set loading to true while we validate with backend
                setIsLoading(true);
                try {
                    const token = await getAccessToken();
                    const accountUpn = accounts[0].username;
                    await fetchUserInfo(token, accountUpn);
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to initialize user:', error);
                    setUser(null);
                } finally {
                    setIsLoading(false);
                }
            } else if (!isAuthenticated && inProgress === InteractionStatus.None) {
                setUser(null);
                setIsLoading(false);
            }
        };

        initializeUser();
    }, [isAuthenticated, accounts, inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Attempt silent SSO on load when not authenticated.
     * This enables automatic sign-in for users who have an existing session.
     */
    useEffect(() => {
        const attemptSilentSso = async () => {
            // Skip if already authenticated, SSO already attempted, or interaction in progress
            if (isAuthenticated || ssoAttempted.current || inProgress !== InteractionStatus.None) {
                return;
            }

            ssoAttempted.current = true;
            setIsSsoAttempting(true);

            try {
                await instance.ssoSilent(loginRequest);
                // If successful, MSAL will update accounts and trigger the user init useEffect
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    // Expected case - no existing session, user needs to login interactively
                    // Silently ignore this error
                } else {
                    // eslint-disable-next-line no-console
                    console.error('Silent SSO failed:', error);
                }
            } finally {
                setIsSsoAttempting(false);
            }
        };

        attemptSilentSso();
    }, [isAuthenticated, inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Initiate login flow using MSAL popup.
     * Works for all users (home tenant and federated organizations).
     *
     * @param loginHint - Optional email address to pre-populate the Azure AD login form
     * @param forceAccountSelection - If true, forces the account picker to show
     */
    const login = async (loginHint?: string, forceAccountSelection?: boolean): Promise<void> => {
        try {
            await instance.loginPopup({
                ...loginRequest,
                ...(loginHint && { loginHint }),
                ...(forceAccountSelection && { prompt: 'select_account' }),
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Login failed:', error);
            throw error;
        }
    };

    /**
     * Logout user from MSAL and clear session.
     */
    const logout = async (): Promise<void> => {
        try {
            // Call backend logout endpoint first (for audit logging)
            try {
                const token = await getAccessToken();
                await fetch(`${BACKEND_URL}/api/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
            } catch (error) {
                // Backend logout failed, continue with MSAL logout
                // eslint-disable-next-line no-console
                console.error('Backend logout failed:', error);
            }

            // Logout from MSAL
            if (accounts.length > 0) {
                await instance.logoutPopup({
                    account: accounts[0],
                });
            }

            setUser(null);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Logout failed:', error);
            throw error;
        }
    };

    /**
     * Switch to a different account.
     * Logs out the current account and triggers login with account picker.
     */
    const switchAccount = async (): Promise<void> => {
        // Clear any existing auth error
        setAuthError(null);

        try {
            // Log out current account (skip backend logout since we're switching)
            if (accounts.length > 0) {
                await instance.logoutPopup({
                    account: accounts[0],
                });
            }

            // Trigger login with account picker
            await instance.loginPopup({
                ...loginRequest,
                prompt: 'select_account',
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Switch account failed:', error);
            throw error;
        }
    };

    // Only consider authenticated when BOTH MSAL has accounts AND backend validated the user
    const isFullyAuthenticated = isAuthenticated && user !== null;

    const contextValue: AuthContextType = {
        user,
        isAuthenticated: isFullyAuthenticated,
        isLoading,
        isSsoAttempting,
        authError,
        login,
        logout,
        switchAccount,
        getAccessToken,
        clearAuthError,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to access authentication context.
 *
 * Must be used within AuthProvider.
 *
 * @returns Authentication context
 * @throws Error if used outside AuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
